import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, WidgetValues, IValueViewModel, ErrorLevel, AggregationMode, TagDateValue, Dictionary } from '../../models';
import { ISeriesSelectorService, ISeriesService, IDateRangeService, IGroupSelectionService, INameBuilderService, IScatterPlotService, IDashboardLoadService, IExportToCsvService } from '../../services';
import { groupSelected, refreshClick, WideError, missingSeries, guidEmpty, toDictionary } from '../../common';
import { ChartConfigurationOption, ShapeFormat } from '../../wtFramework';
import { ScatterWidget } from './scatter.widget';
import { SeriesConfiguration } from '../../widgets';

class ScatterController implements angular.IComponentController {
    boundData: WidgetValues;
    data: any[];
    id: string;
    isLoading: boolean;
    item: ScatterWidget;
    loadingMessage: string;
    options: ChartConfigurationOption;
    series: any[];
    shapes: ShapeFormat[];

    readonly disableCrosshair: boolean = false;
    readonly ratio: number;
    readonly viewModels: Dictionary<IValueViewModel>;

    private _xSeries: SeriesConfiguration;

    static $inject = [
        '$scope',
        '$q',
        'seriesSelectorService',
        'seriesService',
        'dateRangeService',
        'nameBuilderService',
        'ScatterPlotService',
        'dashboardLoadService',
        'exportToCsvService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$q: angular.IQService,
        private _seriesSelectorService: ISeriesSelectorService,
        private _seriesService: ISeriesService,
        private _dateRangeService: IDateRangeService,
        private _nameBuilderService: INameBuilderService,
        private _scatterPlotService: IScatterPlotService,
        private _dls: IDashboardLoadService,
        private _exportToCsvService: IExportToCsvService
    ) {
    }

    $onInit() {
        this.id = `id${this.item.guid}`;
        this.data = [];
        let configuration = this.item.configuration;
        this.options = configuration.options;
        this.series = [];

        const watchers = [];
        this._$scope.$watch(() => this.item, () => {
            if (watchers.length) {
                watchers.forEach(w => w());
            }
            configuration = this.item.configuration;
            watchers.push(this._$scope.$watch(() => configuration.series, (newValue) => {
                this.updateSeries(newValue);
            }, true));
            watchers.push(this._$scope.$watch(() => configuration.shapes, (newValue) => {
                this.refreshValues(this._dateRangeService.beginDate() as string, this._dateRangeService.endDate() as string);
            }, true));
            watchers.push(this._$scope.$watch(() => this.item.configuration.nameConfiguration, () => {
                this.updateSeries(this.item.configuration.series);
            }, true));
        });
        watchers.push(this._$scope.$watch(() => configuration.series, (newValue) => {
            this.updateSeries(newValue);
        }, true));
        watchers.push(this._$scope.$watch(() => configuration.shapes, (newValue) => {
            this.refreshValues(this._dateRangeService.beginDate() as string, this._dateRangeService.endDate() as string);
        }, true));
        watchers.push(this._$scope.$watch(() => this.item.configuration.nameConfiguration, () => {
            this.updateSeries(this.item.configuration.series);
        }, true));


        this._$scope.$on(groupSelected, () => {
            this.updateSeries(this.item.configuration.series);
        });

        if (!this.boundData) {
            this._$scope.$on(refreshClick, this.onRefreshInputValue());
        } else {
            this._$scope.$watch(() => this.boundData, () => {
                if (this.boundData && this.boundData.values) {
                    this.arrangeData(this.boundData.values)
                        .then(data => this.data = data);
                }
            });
        }
        if (this.item) {
            this.item.exportToCsv = () => {
                return this._exportToCsvService.exportToCsv(this.series.concat([this._xSeries]), this.boundData.values.concat(this.boundData.aggregatedValues), this.item.name);
            };
        }
    }

    private updateSeries(seriesToUpdate: SeriesConfiguration[]) {
        this.loadingMessage = 'Loading series';
        this.isLoading = true;
        if (seriesToUpdate && seriesToUpdate.length) {
            const xSeries = _.find(seriesToUpdate, s => s.isXAxis);
            if (xSeries) {
                this._$q.when(seriesToUpdate)
                    .then(data => {
                        this._xSeries = xSeries;
                        const promises: angular.IPromise<any>[] = [
                            this._seriesService.createSeriesList(
                                data.filter(d => !d.isXAxis),
                                this.item.configuration.nameConfiguration,
                                this.viewModels
                            ),
                            this._seriesSelectorService.getViewModel(xSeries ? xSeries.guid : guidEmpty)
                        ];
                        return this._$q.all(promises);
                    })
                    .then(responses => {
                        const seriesVm: IValueViewModel = responses[1];
                        const series: any[] = responses[0];
                        if (_.some(series, s => !s.name)) {
                            this.item.setError(new WideError(ErrorLevel.Error, missingSeries));
                        }
                        this.series = series.filter(s => !!s.name);
                        const xAxisTitle = this._nameBuilderService
                            .getName(seriesVm, this.item.configuration.nameConfiguration, xSeries as any);
                        this.options.xAxis.title = xAxisTitle ? xAxisTitle : 'missing';
                        return this.getxAxisGuid(xSeries);
                    })
                    .then(guid => {
                        const yAxisSeries = this.series.filter(s => !s.isXAxis);
                        if (yAxisSeries.length > 0) {
                            this.options.yAxis.title = yAxisSeries[0].name;
                        } else {
                            this.options.yAxis.title = 'missing';
                        }
                        this.options.xAxis.guid = guid;
                        this.isLoading = false;
                        if (!this.boundData) {
                            this.refreshValues(this._dateRangeService.beginDate() as string, this._dateRangeService.endDate() as string);
                        } else {
                            this.arrangeData(this.boundData.values)
                                .then(data => this.data = data);
                        }
                    })
                    .catch(() => {
                        this.isLoading = false;
                    });
            } else {
                this.series = [];
                this.options.xAxis.title = 'missing';
                this.isLoading = false;
            }
        } else {
            this.isLoading = false;
        }
    }

    private getxAxisGuid(xSeries): angular.IPromise<string> {
        const defered = this._$q.defer<string>();
        if (xSeries) {
            defered.resolve(xSeries.guid);
        } else {
            defered.resolve(null);
        }

        return defered.promise;
    }

    private onRefreshInputValue() {
        const self = this;
        return (event, data) => {
            self.refreshValues(data.beginDate, data.endDate);
        };
    }

    private refreshValues(beginDate, endDate) {
        if (this._dateRangeService.initialized()) {
            this.loadingMessage = 'Loading data';
            this.isLoading = true;
            this._dls
                .refresh([this.item], this._dateRangeService.beginDate() as string, this._dateRangeService.endDate() as string)
                .then(data => {
                    const current = data[0];
                    this.shapes =
                        this._scatterPlotService.mapShapes(this.item.configuration.shapes, current.aggregatedValue);
                    return this.arrangeData(this.item.configuration.aggregation.mode === AggregationMode.None
                        ? current.values
                        : current.aggregatedValues);
                })
                .then(data => {
                    this.data = data;
                })
                .finally(() => this.isLoading = false);
        }
    }

    private arrangeData(data: TagDateValue[]): angular.IPromise<any> {
        // Always recompute as series could have been changed
        (this._xSeries as any).selector = (v: TagDateValue) => v.guid + v.valueType;
        (this._xSeries as any).identifier = this._xSeries.guid + this._xSeries.value;
        return this._$q.when(this._scatterPlotService.arrangeData(data, this._xSeries, this.series));
    }
}

export const wtScatter: ComponentDefinition = {
    componentId: 'wtScatter',
    controller: ScatterController,
    template: require('./scatter.component.html'),
    controllerAs: 'vm',
    bindings: {
        boundData: '=?',
        disableCrosshair: '<?',
        item: '=',
        ratio: '<?',
        viewModels: '<'
    }
};